/**
 * HERO "Aufgaben" module — 1:1 port of the prototype `isAufgaben` blocks
 * (sidebar ~L394, 4-column kanban content ~L729) of
 * `website redesign (delete later)/project/Hausverwaltung Hero.dc.html`.
 *
 * Layout: a 220px (SIDEBAR_WIDTH) sidebar + flex content at MODULE_HEIGHT.
 * Verbatim German copy, exact px/colors, lucide-react icons.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  ArrowUpDown,
  Building2,
  CircleCheck,
  Clock,
  ListFilter,
  Plus,
  SquareKanban,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { SidebarRow } from "../../hero-primitives-app";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Message key inside `marketing.moduleAufgaben` for a card title. */
type CardTitleKey =
  | "angebotDachrinneEinholen"
  | "heizungswartungBeauftragen"
  | "wasserschadenKuecheKoordinieren"
  | "nebenkosten2024RueckfrageEigentuemer"
  | "etvProtokollVersendet";

/** Message key inside `marketing.moduleAufgaben` for a card's trailing chip. */
type CardMetaKey = "sonnenallee12" | "k28Juni";

/** Message key inside `marketing.moduleAufgaben` for a column header. */
type ColumnLabelKey = "offen" | "arbeit" | "wartetAntwort" | "erledigt";

/** A single kanban card. */
type KanbanCard = {
  readonly titleKey: CardTitleKey;
  readonly done?: boolean;
  readonly initials: string;
  /** Trailing chip: property (default) or an amber due-date pill. */
  readonly meta:
    | { readonly kind: "property"; readonly labelKey: CardMetaKey }
    | { readonly kind: "due"; readonly labelKey: CardMetaKey };
};

/** A kanban column: colored label, count, and its cards. */
type KanbanColumn = {
  readonly labelKey: ColumnLabelKey;
  readonly fg: string;
  readonly bg: string;
  readonly count: number;
  readonly cards: readonly KanbanCard[];
};

const COLUMNS: readonly KanbanColumn[] = [
  {
    labelKey: "offen",
    fg: ld("#5B6472", "#b2b6bb"),
    bg: ld("#EFF1F4", "#2c2f33"),
    count: 2,
    cards: [
      {
        titleKey: "angebotDachrinneEinholen",
        initials: "JT",
        meta: { kind: "property", labelKey: "sonnenallee12" },
      },
      {
        titleKey: "heizungswartungBeauftragen",
        initials: "JT",
        meta: { kind: "property", labelKey: "sonnenallee12" },
      },
    ],
  },
  {
    labelKey: "arbeit",
    fg: ld("#3F6CB0", "#7cacf5"),
    bg: ld("#EAF0F8", "#2b3037"),
    count: 1,
    cards: [
      {
        titleKey: "wasserschadenKuecheKoordinieren",
        initials: "SK",
        meta: { kind: "property", labelKey: "sonnenallee12" },
      },
    ],
  },
  {
    labelKey: "wartetAntwort",
    fg: ld("#9A7B1F", "#c7a752"),
    bg: ld("#FBF3DC", "#332c18"),
    count: 1,
    cards: [
      {
        titleKey: "nebenkosten2024RueckfrageEigentuemer",
        initials: "JT",
        meta: { kind: "due", labelKey: "k28Juni" },
      },
    ],
  },
  {
    labelKey: "erledigt",
    fg: ld("#3A7D5A", "#79bc96"),
    bg: ld("#E7F2EB", "#29332d"),
    count: 1,
    cards: [
      {
        titleKey: "etvProtokollVersendet",
        done: true,
        initials: "SK",
        meta: { kind: "property", labelKey: "sonnenallee12" },
      },
    ],
  },
];

/** Sidebar: title, "Meine Aufgaben", and the two boards. */
function AufgabenSidebar(): ReactNode {
  const t = useTranslations("marketing.moduleAufgaben");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
        background: ld("#FBFBFC", "#1d1f23"),
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 6px 12px",
        }}
      >
        <span
          style={{
            fontFamily: H.sans,
            fontSize: 16,
            fontWeight: 700,
            color: H.ink,
          }}
        >
          {t("aufgaben")}
        </span>
      </div>

      <SidebarRow
        icon={<CircleCheck width={16} height={16} strokeWidth={1.8} />}
        label={t("meineAufgaben")}
        count={5}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          color: H.inkGhost,
          textTransform: "uppercase",
          padding: "18px 9px 8px",
        }}
      >
        <span>{t("boards")}</span>
        <Plus width={13} height={13} strokeWidth={1.9} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SidebarRow
          icon={<SquareKanban width={16} height={16} strokeWidth={1.9} />}
          label={t("verwaltung2025")}
          active
        />
        <SidebarRow
          icon={<SquareKanban width={16} height={16} strokeWidth={1.8} />}
          label={t("wegSonnenallee")}
        />
      </div>
    </div>
  );
}

/** A single kanban card (checkbox + title, then meta chip + assignee). */
function Card(props: { readonly card: KanbanCard }): ReactNode {
  const { card } = props;
  const t = useTranslations("marketing.moduleAufgaben");
  return (
    <div
      style={{
        background: ld("#fff", "#181b1f"),
        border: `1px solid ${ld("#ECEEF1", "#2e3135")}`,
        borderRadius: 10,
        padding: "11px 12px",
        boxShadow: `0 1px 2px ${ld("rgba(11,15,25,0.05)", "rgba(0,0,0,0.125)")}`,
        display: "flex",
        flexDirection: "column",
        gap: 9,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
        {card.done === true ? (
          <span
            style={{
              width: 15,
              height: 15,
              borderRadius: "50%",
              background: "#3A9E6B",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              flexShrink: 0,
              marginTop: 1,
            }}
          >
            ✓
          </span>
        ) : (
          <span
            style={{
              width: 15,
              height: 15,
              borderRadius: "50%",
              border: `1.5px solid ${ld("#C9CDD4", "#4b4e52")}`,
              boxSizing: "border-box",
              flexShrink: 0,
              marginTop: 1,
            }}
          />
        )}
        <span
          style={{
            fontFamily: H.sans,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.35,
            color: card.done === true ? H.inkDim : H.ink,
            textDecoration: card.done === true ? "line-through" : "none",
          }}
        >
          {t(card.titleKey)}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {card.meta.kind === "due" ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 8px",
              background: ld("#FBF3DC", "#332c18"),
              borderRadius: 6,
              fontFamily: H.sans,
              fontSize: 11,
              color: ld("#9A7B1F", "#c7a752"),
            }}
          >
            <Clock width={11} height={11} strokeWidth={1.9} />
            {t(card.meta.labelKey)}
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 8px",
              border: `1px solid ${ld("#EBEDF0", "#2f3236")}`,
              borderRadius: 6,
              fontFamily: H.sans,
              fontSize: 11,
              color: H.inkMute,
            }}
          >
            <Building2 width={11} height={11} strokeWidth={1.9} />
            {t(card.meta.labelKey)}
          </span>
        )}
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: H.ink,
            color: ld("#fff", "#181b1f"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: H.sans,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {card.initials}
        </span>
      </div>
    </div>
  );
}

/** A kanban column: header (colored label + count) + cards + "Aufgabe" adder. */
function Column(props: { readonly column: KanbanColumn }): ReactNode {
  const t = useTranslations("marketing.moduleAufgaben");
  const { column } = props;
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 2px",
        }}
      >
        <span
          style={{
            fontFamily: H.sans,
            fontSize: 12,
            fontWeight: 600,
            color: column.fg,
            background: column.bg,
            padding: "3px 10px",
            borderRadius: 6,
          }}
        >
          {t(column.labelKey)}
        </span>
        <span
          style={{
            fontFamily: H.sans,
            fontSize: 12,
            color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
          }}
        >
          {column.count}
        </span>
      </div>
      {column.cards.map((card) => (
        <Card key={card.titleKey} card={card} />
      ))}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 4px",
          fontFamily: H.sans,
          fontSize: 12.5,
          color: ld("rgba(11,15,25,0.38)", "rgba(250,250,250,0.38)"),
          cursor: "pointer",
        }}
      >
        <Plus width={14} height={14} strokeWidth={1.9} />
        {t("aufgabe")}
      </div>
    </div>
  );
}

/**
 * HERO "Aufgaben" module — board picker sidebar + a 4-column kanban
 * ("Verwaltung 2025").
 */
export function ModuleAufgaben(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleAufgaben");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <AufgabenSidebar />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              fontFamily: H.sans,
              fontSize: 18,
              fontWeight: 700,
              color: H.ink,
            }}
          >
            <span
              style={{ color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"), display: "inline-flex" }}
            >
              <SquareKanban width={18} height={18} strokeWidth={1.9} />
            </span>
            {t("verwaltung2025")}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontFamily: H.sans,
              fontSize: 13,
              color: H.inkMute,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 11px",
                borderRadius: 8,
                cursor: "default",
              }}
            >
              <ListFilter width={14} height={14} strokeWidth={1.8} />
              {t("filter")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 11px",
                borderRadius: 8,
                cursor: "default",
              }}
            >
              <ArrowUpDown width={14} height={14} strokeWidth={1.8} />
              {t("sortierung")}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {COLUMNS.map((column) => (
            <Column key={column.labelKey} column={column} />
          ))}
        </div>
      </div>
    </div>
  );
}
